import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useAuthor } from '@/hooks/useAuthor';
import { useVideosByAuthor } from '@/hooks/useVideoFeed';
import { useDeleteVideo } from '@/hooks/useDeleteVideo';
import { Layout } from '@/components/Layout';
import { UploadModal } from '@/components/UploadModal';
import { VideoCard, VideoCardSkeleton } from '@/components/VideoCard';
import { UserActionButtons } from '@/components/UserActionButtons';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import type { NostrEvent } from '@nostrify/nostrify';

export default function ProfilePage() {
  const { pubkey } = useParams<{ pubkey: string }>();
  const [uploadOpen, setUploadOpen] = useState(false);
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const { mutate: deleteVideo } = useDeleteVideo();

  const author = useAuthor(pubkey);
  const meta = author.data?.metadata;

  const { data: videos, isLoading: videosLoading } = useVideosByAuthor(pubkey ?? '');

  const displayName = meta?.name ?? meta?.display_name ?? pubkey?.slice(0, 16) + '…';
  const isOwnProfile = user?.pubkey === pubkey;

  const handleDelete = (event: NostrEvent) => {
    deleteVideo(event, {
      onSuccess: () => toast({ title: 'Video deleted' }),
      onError: (err) => toast({ title: 'Delete failed', description: String(err), variant: 'destructive' }),
    });
  };

  useSeoMeta({
    title: `${displayName} – Goblinbox`,
    description: meta?.about ?? `${displayName}'s videos on Goblinbox.`,
  });

  return (
    <Layout onUploadClick={() => setUploadOpen(true)}>
      {/* Banner */}
      <div
        className="w-full"
        style={{
          height: 180,
          background: meta?.banner
            ? `url(${meta.banner}) center/cover no-repeat`
            : 'linear-gradient(135deg, #1a3a1a 0%, #0f0f0f 100%)',
        }}
      />

      {/* Profile info */}
      <div className="px-4 sm:px-8" style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 pb-4">
          {author.isLoading ? (
            <Skeleton
              className="h-24 w-24 rounded-full shrink-0"
              style={{ background: 'var(--bg-secondary)', border: '4px solid var(--bg-primary)' }}
            />
          ) : (
            <Avatar
              className="h-24 w-24 shrink-0"
              style={{ border: '4px solid var(--bg-primary)', background: 'var(--bg-secondary)' }}
            >
              <AvatarImage src={meta?.picture} />
              <AvatarFallback className="text-3xl font-bold" style={{ background: 'var(--gb-accent)', color: '#fff' }}>
                {displayName[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
          )}

          <div className="flex-1 min-w-0 sm:pb-2">
            {author.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-6 w-48" style={{ background: 'var(--bg-secondary)' }} />
                <Skeleton className="h-4 w-32" style={{ background: 'var(--bg-secondary)' }} />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{displayName}</h1>
                  {meta?.nip05 && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: 'var(--bg-secondary)', color: '#1da1f2' }}
                    >
                      ✓ {meta.nip05}
                    </span>
                  )}
                </div>
                {meta?.about && (
                  <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                    {meta.about}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {videos && (
                    <span>{videos.length} video{videos.length !== 1 ? 's' : ''}</span>
                  )}
                  {meta?.website && (
                    <a
                      href={meta.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline truncate max-w-xs"
                      style={{ color: 'var(--gb-accent)' }}
                    >
                      {meta.website.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Action buttons: upload (own profile) or follow/mute/block (other profiles) */}
          {isOwnProfile ? (
            <button
              onClick={() => setUploadOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded font-medium text-sm text-white transition-all hover:opacity-90 shrink-0"
              style={{ background: 'var(--gb-accent)' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
              </svg>
              Upload Video
            </button>
          ) : pubkey ? (
            <div className="shrink-0">
              <UserActionButtons pubkey={pubkey} displayName={displayName} />
            </div>
          ) : null}
        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px solid var(--gb-border)', marginBottom: '1.5rem' }} />

        {/* Videos grid */}
        <h2
          className="text-xs font-bold uppercase tracking-wider mb-4"
          style={{ color: 'var(--text-secondary)' }}
        >
          Videos
        </h2>

        {videosLoading ? (
          <div className="video-grid">
            {Array.from({ length: 6 }).map((_, i) => <VideoCardSkeleton key={i} />)}
          </div>
        ) : videos && videos.length > 0 ? (
          <div className="video-grid">
            {videos.map(event => (
              <VideoCard
                key={event.id}
                event={event}
                onDelete={isOwnProfile ? handleDelete : undefined}
              />
            ))}
          </div>
        ) : (
          <div
            className="rounded-xl py-16 px-8 text-center"
            style={{ border: '2px dashed var(--gb-border)' }}
          >
            <div className="text-5xl mb-4">📹</div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {isOwnProfile
                ? "You haven't uploaded any videos yet."
                : `${displayName} hasn't uploaded any videos yet.`}
            </p>
            {isOwnProfile && (
              <button
                onClick={() => setUploadOpen(true)}
                className="mt-4 px-4 py-2 rounded font-medium text-sm text-white hover:opacity-90 transition-all"
                style={{ background: 'var(--gb-accent)' }}
              >
                Upload your first video
              </button>
            )}
          </div>
        )}
      </div>

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </Layout>
  );
}
